<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { toast } from 'vue-sonner';
import {
    AdjustmentsHorizontalIcon,
    ArrowTopRightOnSquareIcon,
    DocumentDuplicateIcon,
    EllipsisHorizontalIcon,
} from '@heroicons/vue/24/outline';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LazyTooltip } from '@/components/ui/tooltip';
import { adminAppEntityFor, adminAppRecordUrl } from '@/utils/adminAppLink';
import { copyText } from '@/utils/nativeShell';

/**
 * The record's ⋯ menu: "Open in Admin app", "Copy ID 141" and "Customize
 * page…". The record page, the record pane and the peeks each show one, so a
 * record offers the same actions wherever it is open. Customize lives here,
 * as Macro's "Customize sidebar" lives in its More menu.
 *
 * The tooltip wraps the whole menu, not just its trigger, so the menu keeps
 * its anchor. See LazyTooltip for why.
 */
const props = withDefaults(
    defineProps<{
        entityId: string;
        recordId: number | string;
        /** The record noun as a heading reads it, such as "Account", for the copy toast. */
        noun: string;
        /** Prefix for the test ids, such as "record-more" on the page. */
        testIdPrefix: string;
        /** "page" is the record page's 32 pixel button. "compact" is the pane's and the peek's 28 pixel one. */
        size?: 'page' | 'compact';
        /** Offer "Customize page…". Off where the record has no layout to customize. */
        canCustomize?: boolean;
    }>(),
    { size: 'page', canCustomize: true },
);

const emit = defineEmits<{ customize: [] }>();

const adminAppHref = computed(() => {
    const adminAppEntity = adminAppEntityFor(props.entityId);
    return adminAppEntity === '' ? null : adminAppRecordUrl(adminAppEntity, props.recordId);
});

async function copyId() {
    if (await copyText(String(props.recordId))) {
        toast.success(`Copied ${props.noun.toLowerCase()} ID ${props.recordId}.`);
    } else {
        toast.error(`Couldn't copy the ID. It is ${props.recordId}.`);
    }
}

/**
 * Choosing "Customize page…" opens a dialog. The menu would hand focus back
 * to its button as it closes, and pull it out of the dialog, so it is told
 * not to. The dialog hands focus back to the button when it closes.
 */
let customizeChosen = false;

function chooseCustomize() {
    customizeChosen = true;
    emit('customize');
}

function onCloseAutoFocus(event: Event) {
    if (customizeChosen) {
        event.preventDefault();
        customizeChosen = false;
    }
}

const trigger = useTemplateRef<HTMLButtonElement>('trigger');

defineExpose({
    /** Puts focus back on the ⋯ button, for when the Customize dialog closes. */
    focusTrigger: () => trigger.value?.focus(),
});
</script>

<template>
  <LazyTooltip side="bottom" align="end">
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <button
          ref="trigger"
          type="button"
          aria-label="More actions"
          :data-testid="testIdPrefix"
          class="flex shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
          :class="size === 'page' ? 'size-8' : 'size-7'"
        >
          <EllipsisHorizontalIcon class="size-4" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" class="min-w-52" @close-auto-focus="onCloseAutoFocus">
        <DropdownMenuItem v-if="adminAppHref" as-child>
          <a :href="adminAppHref" class="flex items-center gap-2" :data-testid="`${testIdPrefix}-admin-app`">
            <ArrowTopRightOnSquareIcon class="size-4 text-muted-foreground" aria-hidden="true" />
            Open in Admin app
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem class="flex items-center gap-2" :data-testid="`${testIdPrefix}-copy-id`" @select="copyId">
          <DocumentDuplicateIcon class="size-4 text-muted-foreground" aria-hidden="true" />
          Copy ID {{ recordId }}
        </DropdownMenuItem>
        <template v-if="canCustomize">
          <DropdownMenuSeparator />
          <DropdownMenuItem class="flex items-center gap-2" :data-testid="`${testIdPrefix}-customize`" @select="chooseCustomize">
            <AdjustmentsHorizontalIcon class="size-4 text-muted-foreground" aria-hidden="true" />
            Customize page…
          </DropdownMenuItem>
        </template>
      </DropdownMenuContent>
    </DropdownMenu>
    <template #content>More actions</template>
  </LazyTooltip>
</template>
